import { Button } from '@ai-tutor/ui';
import { ArrowRight } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import type { UserRole } from '@/lib/api-client';

import { RoleSelector } from '../RoleSelector';
import { StepLayout } from '../StepLayout';

interface RoleStepProps {
  /** Owned by the wizard so it survives navigating back from later steps. */
  role?: UserRole;
  onRoleChange: (role: UserRole) => void;
  onContinue: () => void;
}

/**
 * Step 1 of 4 — the only answer onboarding actually needs from the user.
 *
 * Role has no default, because guessing it would silently mis-personalize
 * everything downstream. Depth and flow do have defaults, which is exactly why
 * they are no longer on this screen: one required question and two
 * already-answered ones, at identical visual weight, gave the user no way to
 * tell what was being asked of them. Splitting them lets this screen mean
 * "the thing we need" and the next one mean "what we have already set".
 *
 * Nothing is persisted here. Role is held by the wizard and written with the
 * `onboarded` flip at the end, so abandoning the flow leaves no partial state.
 */
export function RoleStep({ role, onRoleChange, onContinue }: RoleStepProps) {
  const [roleError, setRoleError] = useState<string>();
  const roleErrorId = useId();
  const roleGroup = useRef<HTMLDivElement>(null);

  const handleContinue = () => {
    if (!role) {
      setRoleError('Please pick one to continue');
      // Continue stays ENABLED with nothing chosen — a disabled button never
      // says why it is disabled. That only works if the refusal is perceivable,
      // so move focus into the group the message describes: it puts the error
      // in reach of a screen reader (which announces it via `aria-describedby`
      // on arrival, on top of the `role='alert'` live announcement) and, on a
      // narrow viewport where the question has scrolled off above the button,
      // scrolls it back into view.
      const group = roleGroup.current;
      const target =
        group?.querySelector<HTMLElement>('[role="radio"][tabindex="0"]') ??
        group?.querySelector<HTMLElement>('[role="radio"]');
      target?.focus();
      return;
    }
    onContinue();
  };

  return (
    <StepLayout
      title='Let’s set up your space'
      description='Two quick questions. You can change your answers anytime in Settings.'
      actions={
        <Button type='button' onClick={handleContinue}>
          Continue
          <ArrowRight className='ml-1.5 h-4 w-4' />
        </Button>
      }
    >
      <section>
        <RoleSelector
          value={role}
          onChange={r => {
            onRoleChange(r);
            setRoleError(undefined);
          }}
          errorId={roleError ? roleErrorId : undefined}
          containerRef={roleGroup}
        />
        {/* `text-destructive`, not `text-red-600`: raw red-600 measured 3.94:1
            against the page ground at 12px and failed WCAG AA. The token is
            contrast-corrected per theme. `role='alert'` because the message
            appears in response to a click and is the only feedback the user
            gets — without it the refusal was completely silent. */}
        {roleError && (
          <p
            id={roleErrorId}
            role='alert'
            className='mt-2 text-label text-destructive'
          >
            {roleError}
          </p>
        )}
      </section>
    </StepLayout>
  );
}
